import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Star, Trophy, Users, Plus } from "lucide-react";

export default function ScorecardsPage() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newCard, setNewCard] = useState({ sub_user_id: "", project_id: "", timeliness: 5, quality: 5, communication: 5, notes: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [lbRes, teamRes] = await Promise.all([
        axios.get(`${API}/scorecards/leaderboard`),
        axios.get(`${API}/team`)
      ]);
      setLeaderboard(lbRes.data || []);
      setTeamMembers((teamRes.data || []).filter(m => m.role === "subcontractor" || m.role === "employee"));
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newCard.sub_user_id) { toast.error("Select a team member"); return; }
    try {
      await axios.post(`${API}/scorecards`, newCard);
      toast.success("Scorecard submitted");
      setShowAdd(false);
      setNewCard({ sub_user_id: "", project_id: "", timeliness: 5, quality: 5, communication: 5, notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-slate-200"}`} />
    ));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scorecards-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Trophy className="w-6 h-6 text-yellow-500" /> Subcontractor Scorecards</h1>
          <p className="text-slate-600 mt-1">Rate and track team performance</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-scorecard-btn"><Plus className="w-4 h-4 mr-2" /> Rate Team Member</Button>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 ? (
        <div className="space-y-3">
          {leaderboard.map((entry, idx) => (
            <Card key={entry.sub_user_id} className={idx === 0 ? "border-yellow-300 bg-yellow-50/30" : ""} data-testid={`lb-${entry.sub_user_id}`}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${idx === 0 ? "bg-yellow-100 text-yellow-700" : idx === 1 ? "bg-slate-100 text-slate-600" : idx === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-50 text-slate-500"}`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{entry.user?.name || "Unknown"}</p>
                    <p className="text-xs text-slate-500">{entry.total_reviews} review{entry.total_reviews !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Timeliness</p>
                      <div className="flex justify-center">{renderStars(entry.avg_timeliness)}</div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Quality</p>
                      <div className="flex justify-center">{renderStars(entry.avg_quality)}</div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Communication</p>
                      <div className="flex justify-center">{renderStars(entry.avg_communication)}</div>
                    </div>
                  </div>
                  <div className="text-center ml-4">
                    <p className="text-2xl font-bold text-indigo-600">{entry.avg_overall}</p>
                    <p className="text-xs text-slate-500">Overall</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No scorecards yet</p>
          <p className="text-sm text-slate-400">Rate your subcontractors after each job</p>
        </CardContent></Card>
      )}

      {/* Add Scorecard Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Rate Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Team Member</Label>
              <Select value={newCard.sub_user_id} onValueChange={(v) => setNewCard({ ...newCard, sub_user_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{teamMembers.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[
              { key: "timeliness", label: "Timeliness" },
              { key: "quality", label: "Quality of Work" },
              { key: "communication", label: "Communication" },
            ].map(({ key, label }) => (
              <div key={key}>
                <Label>{label}: {newCard[key]}/5</Label>
                <input type="range" min="1" max="5" value={newCard[key]} onChange={(e) => setNewCard({ ...newCard, [key]: parseInt(e.target.value) })} className="w-full accent-indigo-600" />
                <div className="flex justify-between text-xs text-slate-400"><span>Poor</span><span>Excellent</span></div>
              </div>
            ))}
            <div><Label>Notes</Label><Textarea value={newCard.notes} onChange={(e) => setNewCard({ ...newCard, notes: e.target.value })} rows={2} placeholder="Optional feedback..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={handleSubmit}>Submit Rating</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
